import { track } from "./events";

// Problems on a teacher's phone are otherwise invisible: a blank screen or a
// failed save leaves no trace and the data it would have recorded is simply
// missing. Every uncaught error, rejected promise, screen crash and server
// error becomes an app_error event, so a gap in someone's record can be
// explained.

const MAX_PER_PAGE_LOAD = 20;
const REPEAT_WINDOW_MS = 60_000;
const MAX_MESSAGE = 300;
const MAX_STACK = 1500;

export type AppErrorKind = "window_error" | "unhandled_rejection" | "render_crash" | "api_error";

const recent = new Map<string, number>();
let reported = 0;

// Browsers report these for harmless layout work; they say nothing is broken.
const NOISE = [/ResizeObserver loop/i, /^Script error\.?$/i];

function clip(value: unknown, limit: number): string {
  const text =
    typeof value === "string" ? value : value instanceof Error ? value.message : String(value);
  return text.length > limit ? `${text.slice(0, limit)}…` : text;
}

function moduleFromRoute(): string | null {
  const match = window.location.pathname.match(/^\/modules\/([0-9a-f-]{36})/i);
  return match ? match[1] : null;
}

export function reportError(
  kind: AppErrorKind,
  error: unknown,
  extra: Record<string, unknown> = {}
): void {
  const message = clip(error ?? "unknown error", MAX_MESSAGE);
  if (NOISE.some((pattern) => pattern.test(message))) return;
  const key = `${kind}:${message}:${extra.status ?? ""}:${extra.path ?? ""}`;
  const now = Date.now();
  const last = recent.get(key);
  if (last !== undefined && now - last < REPEAT_WINDOW_MS) return;
  if (reported >= MAX_PER_PAGE_LOAD) return;
  recent.set(key, now);
  reported += 1;
  track("app_error", {
    module_id: moduleFromRoute(),
    payload: {
      kind,
      message,
      stack: error instanceof Error && error.stack ? clip(error.stack, MAX_STACK) : null,
      route: window.location.pathname.replace(/[0-9a-f-]{36}/gi, ":id"),
      online: navigator.onLine,
      ...extra,
    },
  });
}

export function installErrorReporting(): void {
  window.addEventListener("error", (event) => {
    // Failed images and scripts fire this on the element, not the window.
    if (event.target && event.target !== window) return;
    reportError("window_error", event.error ?? event.message, {
      source: event.filename ? event.filename.split("/").pop() : null,
      line: event.lineno || null,
      column: event.colno || null,
    });
  });
  window.addEventListener("unhandledrejection", (event) => {
    reportError("unhandled_rejection", event.reason ?? "promise rejected");
  });
  window.addEventListener("cpd:api-error", (event) => {
    const detail = (event as CustomEvent<{ status: number; path: string; message: string }>).detail;
    reportError("api_error", detail.message, { status: detail.status, path: detail.path });
  });
}

// Test hook: forget what was reported so far.
export function resetErrorReporting(): void {
  recent.clear();
  reported = 0;
}
